import { axios } from 'api'
import MobileUserAccessBadge from 'components_v1/aloAccessBadge/MobileUserAccessBadge'
import UserAccessBadge from 'components_v1/aloAccessBadge/UserAccessBadge'
import StyledVideoPlayer from 'components_v1/v2Wrappers/StyledVideoPlayer'
import React, { useCallback, useEffect, useState } from 'react'
import InstructorList from './InstructorList'
import moment from 'moment'
import CommentCount from 'components_v1/comments/CommentCount'
import Description from './Description'
import BuilderSection from 'components_v1/BuilderSection'
import IntroVideos from './IntroVideos'
import ClassActions from './ClassActions'
import PlanBasicInfo from './PlanBasicInfo'
import TagsContainer from './TagsContainer'
import AloversaryPixleeWidget from './AloversaryPixleeWidget'
import BottomWidgetTempEntry from 'components_v1/bottomWidget/BottomWidgetTempEntry'
import CommentSection from 'components_v1/comments/CommentSection'
import Ga4Push from './Ga4Push'
import styled from 'styled-components'
import { useWindowResize } from 'hooks'
import { useParams } from 'react-router-dom'
import { useMeta, useTitle } from 'hooks/useMetaTitle'
import { useCommonEntryContext } from 'components_v2/common'
import NotAvailable from 'components_v2/_entryPoints/grouped/NotAvailable'
import CommonLink from 'components_v2/CommonLink'
import { ClassViewContext } from 'components_v2/page_context'
import MarkComplete from './MarkComplete'

const Main = styled.div`
  .no-description {
    justify-content: center;
    .workout-information {
      display: none;
    }
  }
  .info-container {
    width: 100%;
    gap: 16px;
  }
  @media screen and (min-width: 768px) {
    .pdp-class-actions__scroll {
      display: flex !important;
      flex-wrap: nowrap;
      gap: ${(state) => `${state.actionGap}px`};
    }
  }
  .coverPhoto {
    margin: 0 auto;
    img {
      aspect-ratio: 16/9;
      object-fit: cover;
    }
  }
`

const initLoaded = {
  data: false,
  plan_entry: false
}

const ClassView = () => {
  const { plan_entry_id: id } = useParams()
  const [loaded, setLoaded] = useState({ ...initLoaded })
  const { user } = useCommonEntryContext()
  const [classState, setClassState] = useState({})
  const [plan_entry, setPlanEntry] = useState(null)
  const [videoMap, setVideoMap] = useState({})
  const plan = plan_entry?.plan
  const singleClass = Boolean(plan?.singleClass)
  const [props, setProps] = useState()

  useTitle(props?.classTitle)
  useMeta([{ name: 'description', content: plan_entry?.description }])
  const fetchMetaData = useCallback((id) => {
    setLoaded((state) => ({ ...state, data: false }))
    axios
      .get(`/plan_entries/data/${id}`, { load: true })
      .then((res) => {
        const data = res.data
        if (data) {
          setProps({
            ...data,
            introVideo: data?.introVideo || [],
            playlistIds: data?.playlistIds || []
          })
          setLoaded((state) => ({ ...state, data: true }))
        }
      })
      .catch((err) => {
        console.log(err)
      })
  }, [])
  useEffect(() => {
    fetchMetaData(id)
    return () => {
      window.document.body.classList?.remove('modal-open')
      document.querySelectorAll('.modal-backdrop').forEach((el) => {
        el.remove()
      })
    }
  }, [fetchMetaData, id])
  const fetchData = useCallback(() => {
    setPlanEntry(null)
    setClassState({})
    setLoaded((state) => ({ ...state, plan_entry: false }))
    axios
      .get(`/plan_entries/${id}`, { load: true })
      .then((res) => {
        if (res.data) {
          setPlanEntry(res.data)
          setClassState((state) => ({ ...state, ...res.data }))
          setLoaded((state) => ({ ...state, plan_entry: true }))
        }
      })
      .catch((err) => console.log(err))
  }, [id])
  useEffect(() => {
    fetchData()
  }, [fetchData])
  const video = plan_entry?.video
  const showVideoPlayer = plan_entry?.unlocked && video
  const classDetails = {
    id: plan_entry?.id,
    style: plan_entry?.style,
    title: plan_entry?.title,
    availabilityGating: plan_entry?.availabilityGating
  }
  const coachList = plan?.coaches?.map((c) => ({
    slug: c?.coachInfo?.slug,
    fullName: [
      c?.coachInfo?.user?.firstName,
      c?.coachInfo?.user?.lastName
    ].join(' ')
  }))

  // code to pause running intro/preview videos on modal close
  useEffect(() => {
    if (!singleClass) return
    const modalObservers = []

    const pauseAllVideos = () => {
      document.querySelectorAll('video').forEach((video, index) => {
        if (index === 0) return
        video.pause()
      })
    }
    const attachModalObserver = (modal) => {
      const observer = new MutationObserver(() => {
        if (window.getComputedStyle(modal).display === 'none') {
          pauseAllVideos()
        }
      })
      observer.observe(modal, {
        attributes: true,
        attributeFilter: ['style', 'class']
      })
      modalObservers.push(observer)
    }
    const attachAllModals = () => {
      const modals = document.querySelectorAll('.video-modal')
      modals.forEach((modal) => attachModalObserver(modal))
    }
    const bodyObserver = new MutationObserver(() => {
      modalObservers.forEach((obs) => obs.disconnect())
      modalObservers.length = 0
      attachAllModals()
    })
    attachAllModals()
    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true
    })

    return () => {
      bodyObserver.disconnect()
      modalObservers.forEach((obs) => obs.disconnect())
    }
  }, [singleClass])

  useEffect(() => {
    const localizedVideos =
      plan_entry?.localizedVideos?.length > 0
        ? plan_entry.localizedVideos
        : [
            {
              language: plan_entry?.video?.language || 'en',
              languageName: plan_entry?.video?.languageName || 'English',
              video: plan_entry?.video
            }
          ]

    setVideoMap((state) => ({ ...state, [id]: localizedVideos }))
  }, [id, plan_entry])

  const { width } = useWindowResize()
  const actionGap = Math.min(16, Math.floor(width / 80))
  const showCoverPhoto = plan_entry?.photo?.url && !video
  const showVideoContainer =
    video?.processStatus === 'uploaded' ||
    video?.processStatus === 'started' ||
    showVideoPlayer ||
    !showCoverPhoto

  if (!plan_entry || Object.values(loaded).includes(false)) return null
  if (props?.softDeleted) return <NotAvailable />

  return (
    <ClassViewContext.Provider
      value={{ fetchData, plan_entry, setClassState, classState }}
    >
      <Main actionGap={actionGap} className='plan-entry video-container'>
        <Ga4Push plan_entry={plan_entry} />
        {showCoverPhoto && (
          <div className='coverPhoto section-container entry-video-section'>
            <img src={plan_entry.photo.url} alt={plan_entry.title} />
          </div>
        )}
        {showVideoContainer && (
          <div className='section-container entry-video-section'>
            <div className='play-video-container workoutVideo video-play-cover'>
              {video?.processStatus === 'uploaded' ||
              video?.processStatus === 'started' ? (
                <img
                  src='https://d28gwrkukqy4h7.cloudfront.net/training/videoProcessing.jpg'
                  alt='processing'
                />
              ) : showVideoPlayer ? (
                Object.keys(videoMap)
                  .filter((k) => Number(k) === Number(id))
                  .map((clId) => (
                    <StyledVideoPlayer
                      key={Number(clId)}
                      videoId={video.id}
                      planEntryId={Number(clId)}
                      videoOriginType='PlanEntry'
                      planId={plan_entry.plan.id}
                      localizedVideos={videoMap[clId]}
                      user={user}
                    />
                  ))
              ) : !showCoverPhoto ? (
                <>
                  <div className='black-overlay'>
                    <div>
                      <UserAccessBadge
                        user={user}
                        classDetails={classDetails}
                      />
                    </div>
                  </div>
                  <img
                    src={video?.thumbnail?.url || plan_entry?.videoThumbnail}
                    alt='video_thumbnail'
                  />
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        )}
        {!showVideoPlayer && !showCoverPhoto && (
          <MobileUserAccessBadge user={user} classDetails={classDetails} />
        )}
        <div className='section-container container ref-container workoutContainer'>
          <div className='section-items-container author-block not-single-class'>
            <div className='workout-information-container colContainer'>
              <div className='info-container'>
                <div className='basic-information'>
                  <div className='page-type'>
                    <h2>Class</h2>
                  </div>
                  <div className='workoutTitle workout video-big-title'>
                    {plan_entry.title}
                  </div>
                  <div className='video-meta-info'>
                    <div>
                      <span className='author-text'>Instructor:</span>
                      <InstructorList list={plan?.coaches} />
                    </div>
                    {!singleClass && (
                      <>
                        <div>
                          <span className='author-text'>Program:</span>{' '}
                          <span className='series-info'>
                            <CommonLink
                              className='link'
                              to={`/series/${plan.slug}`}
                            >
                              {plan.title}
                            </CommonLink>
                          </span>
                        </div>
                      </>
                    )}
                    {plan_entry?.completedAt && (
                      <div>
                        <span className='completed-text'>Last completed:</span>{' '}
                        <span className='completed-date'>
                          {moment(new Date(plan_entry?.completedAt)).format(
                            'MM/DD/YYYY'
                          )}
                        </span>
                      </div>
                    )}
                    {user && (
                      <div>
                        <CommentCount />
                      </div>
                    )}
                    {plan_entry?.showMarkComplete && (
                      <MarkComplete plan_entry_id={id} />
                    )}
                  </div>
                </div>

                <ClassActions
                  user={user}
                  plan_entry={plan_entry}
                  playlistIds={props?.playlistIds}
                />
              </div>
            </div>
          </div>
        </div>
        <IntroVideos
          plan={plan}
          previewEntry={props?.previewEntry}
          introVideo={props?.introVideo}
        />
        {user?.admin && (
          <div className='adminActions'>
            <a
              href={`/admin/plan_entries/${plan_entry.id}/edit`}
              target='_blank'
              className='btn alo-button'
              rel='noreferrer'
            >
              Edit Class
            </a>
          </div>
        )}
        <div
          className={`plan-entry-content ${
            singleClass ? 'section-container' : ''
          } `}
        >
          <div className='tab-content'>
            <div
              role='tabpanel'
              className='tab-pane fade in active'
              id='overview'
            >
              <div className='class-info-container'>
                <div className='descriptionText workoutDescription'>
                  <div
                    className={`info-container ${
                      plan_entry?.description ? '' : 'no-description'
                    }`}
                  >
                    <h4 className='descriptionTitle'>DESCRIPTION</h4>
                    <div className='workout-information'>
                      <Description description={plan_entry.description} />
                    </div>
                  </div>
                  {props?.bonusData && (
                    <div className='bonus-content-section'>
                      <BuilderSection
                        user={{}}
                        builderModelname='bonus-content-section'
                        data={props?.bonusData}
                      />
                    </div>
                  )}
                </div>

                <div className='plan-entry-meta-information'>
                  <PlanBasicInfo plan_entry={plan_entry} />
                  {plan_entry?.contextTags?.length > 0 && (
                    <div className='extra-tags'>
                      <div className='tags-container'>
                        <TagsContainer tags={plan_entry?.contextTags} />
                      </div>
                    </div>
                  )}
                </div>
                {props?.widgetPixleeId && (
                  <AloversaryPixleeWidget
                    widgetPixleeId={props?.widgetPixleeId}
                    apiKey={props?.apiKey}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        {user && (
          <>
            <div className='section-bottom-widget class'>
              <BottomWidgetTempEntry user={user} type='class' id={id} />
            </div>
            {showVideoPlayer && (
              <div className='post-comment' id='class-pdp-post-comment'>
                <CommentSection
                  classDetails={classDetails}
                  videoDetails={{ title: video?.title || '' }}
                  seriesDetails={{ title: plan?.title || '' }}
                  type='CLASS'
                  user={user}
                  coaches={coachList}
                />
              </div>
            )}
          </>
        )}
      </Main>
      {singleClass && (
        <>
          {props?.introVideo?.length > 0 && (
            <div
              className='modal fade video-modal large'
              id='preview-modal'
              tabIndex='-1'
              role='dialog'
            >
              <div className='modal-dialog' role='document'>
                <div className='modal-content video-play-cover'>
                  <div className='plan-video-container'>
                    <StyledVideoPlayer
                      localizedVideos={props?.introVideo}
                      planEntryId={plan_entry.id}
                      user={user}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {props?.previewEntry && (
            <div
              className='modal fade video-modal large'
              id='3-min-modal'
              tabIndex='-1'
              role='dialog'
            >
              <div className='modal-dialog' role='document'>
                <div className='modal-content video-play-cover'>
                  <div className='plan-video-container'>
                    <StyledVideoPlayer videoId={props?.previewEntry?.id} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </ClassViewContext.Provider>
  )
}

export default ClassView
